import { Fragment } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { DETAIL, euro, germanDate, num } from '../lib/euerFormat';

/** Gemeinsam für die Seiten EÜR (D-23) und KSt-Erklärung (D-25). */
export interface EuerGroup {
  groupId: string;
  name: string;
  cents: number;
  accounts: { number: string; name: string; cents: number }[];
}

export interface AssetLine {
  postingId: string;
  voucherNumber?: string;
  text?: string;
  partner?: string;
  acquiredOn: string;
  divisionId?: string;
  accountNumber?: string;
  accountName?: string;
  costCents: number;
  usefulLifeMonths?: number;
  depreciationCents: number;
  bookValueCents: number;
}

export interface VoucherRow {
  postingId: string;
  fiscalYear: string;
  stichtag: string;
  voucherNumber?: string;
  text?: string;
  partner?: string;
  divisionId?: string;
  accountNumber?: string;
  accountName?: string;
  netCents: number;
  hasDocument: boolean;
}

export interface EuerData {
  income: { groups: EuerGroup[]; vatCollected: number; vatRefunds: number; total: number };
  expense: {
    groups: EuerGroup[];
    depreciation: number;
    gwg: number;
    inputVat: number;
    vatPayments: number;
    total: number;
  };
  result: number;
  reconciliation: {
    saldo: number;
    assetPurchases: number;
    depreciation: number;
    vat: number;
    finanzamt: number;
  };
  vouchers: { assetPurchases: VoucherRow[]; gwg: VoucherRow[]; finanzamt: VoucherRow[] };
  assets: AssetLine[];
  missingUsefulLife: number;
}

export function Line({
  label,
  cents,
  level = 0,
  bold,
  detail,
}: {
  label: string;
  cents: number;
  level?: number;
  bold?: boolean;
  /** Einzelzeile, die beim Druck "nur Summen" entfällt */
  detail?: boolean;
}) {
  return (
    <TableRow className={detail ? DETAIL : undefined}>
      <TableCell sx={{ pl: 2 + level * 3 }}>{bold ? <strong>{label}</strong> : label}</TableCell>
      <TableCell {...num}>{bold ? <strong>{euro(cents)}</strong> : euro(cents)}</TableCell>
    </TableRow>
  );
}

function Groups({ groups }: { groups: EuerGroup[] }) {
  return groups.map((g) => (
    <Fragment key={g.groupId}>
      {g.accounts.map((a) => (
        <Line key={a.number} label={`${a.number} ${a.name}`} cents={a.cents} level={2} detail />
      ))}
      <Line label={`Summe ${g.name}`} cents={g.cents} level={1} bold />
    </Fragment>
  ));
}

/** Die EÜR selbst: Gruppen mit Zwischensummen, Abschreibungen, Umsatzsteuer-Block, Ergebnis. */
export function EuerTable({
  e,
  loading,
  refundLabel = 'Vom Finanzamt erstattete Umsatzsteuer',
  paymentLabel = 'Umsatzsteuerzahlungen an das Finanzamt',
}: {
  e: EuerData;
  loading?: boolean;
  refundLabel?: string;
  paymentLabel?: string;
}) {
  return (
    <TableContainer component={Paper} sx={{ opacity: loading ? 0.5 : 1 }}>
      <Table size="small">
        <TableBody>
          <TableRow>
            <TableCell colSpan={2}>
              <Typography variant="h6">Betriebseinnahmen</Typography>
            </TableCell>
          </TableRow>
          <Groups groups={e.income.groups} />
          <Line label="Vereinnahmte Umsatzsteuer" cents={e.income.vatCollected} level={1} />
          <Line label={refundLabel} cents={e.income.vatRefunds} level={1} />
          <Line label="Summe Betriebseinnahmen" cents={e.income.total} bold />

          <TableRow>
            <TableCell colSpan={2}>
              <Typography variant="h6" sx={{ mt: 2 }}>
                Betriebsausgaben
              </Typography>
            </TableCell>
          </TableRow>
          <Groups groups={e.expense.groups} />
          {e.assets
            .filter((a) => a.depreciationCents)
            .map((a) => (
              <Line
                key={a.postingId}
                label={`${a.text ?? a.partner ?? a.voucherNumber ?? 'Anlage'} (${germanDate(a.acquiredOn)})`}
                cents={a.depreciationCents}
                level={2}
                detail
              />
            ))}
          <Line label="GWG (geringwertige Wirtschaftsgüter)" cents={e.expense.gwg} level={2} />
          <Line
            label="Summe Abschreibungen"
            cents={e.expense.depreciation + e.expense.gwg}
            level={1}
            bold
          />
          <Line label="Gezahlte Vorsteuer" cents={e.expense.inputVat} level={1} />
          <Line label={paymentLabel} cents={e.expense.vatPayments} level={1} />
          <Line label="Summe Betriebsausgaben" cents={e.expense.total} bold />

          <TableRow>
            <TableCell>
              <Typography variant="h6">{e.result < 0 ? 'Verlust' : 'Gewinn'}</Typography>
            </TableCell>
            <TableCell {...num}>
              <Typography variant="h6">{euro(e.result)}</Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
